import { Grid, Box, Link } from '@mui/material';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { isEmpty } from 'lodash';
import { useEffect, useState, useRef, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  IWorkspace,
  IWorkspaceUserFilter,
  fetchDashboardCollectionsAssignedToAccountManagers,
  getOverlapTeams,
  getMyPinnedDashboardsCollection,
  getWorkspaceUsers,
  triggerUsersImport,
} from 'wisestep-api';
import {
  WsListingLoader,
  WsTypography,
  WsTypoColors,
  WsButton,
  WsColors,
  WsTypoVariants,
  getDateUsingFormat,
  WsBadge,
  WsBadgeSize,
  WsBadgetype,
} from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard/NoDataFoundCard';
import PinDashboardCollectionButton, {
  DashboardCollectionIdType,
} from '../../../components/pinDashboardCollectionButton/PinDashboardCollectionButton';
import WsContextMenuHandler from '../../../components/WsContextMenu/WsContextMenuHandler';
import { useUserContext } from '../../../context';
import PersonAvatar from '../../../images/Avatar/Person.svg';
import { NORTHHILLPARTNERS_WORKSPACE_ID } from '../../../utils/Constants';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { StyledPaginationBox } from '../../clientsSettings/components/OrganizationalUnitLayoutNew';
import { stringAvatar } from '../../recruiterProfileDetails/components/RecruiterAvatarGroup';
import WsDataGridPagination from '../../submissionsNew/components/widgets/WsDataGridPagination';
import { StyledDataGrid } from '../../teams/components/TeamListNew';
import WsAvatar from '../../teams/components/WsAvatar';
import {
  getUserStatus,
  workspaceAccountManagerPayload,
  workspaceUsersPayload,
} from '../helper/defaultValues';

import UsersFilterDetailsNew from './UserFliterDetailsNew';

export const avatarStyling = (value) => {
  const avatarSize = 28;
  const props = { ...stringAvatar(value) };
  props.sx['width'] = avatarSize;
  props.sx['height'] = avatarSize;
  props.sx['fontSize'] = avatarSize / 2;
  props.sx['cursor'] = 'default';
  return props;
};

interface IUsersDataStatus {
  workspaceUsers: IWorkspace[];
  totalUsers: number;
}

const AllUsersInTeamsNew = ({
  forAccountManagerListing,
}: {
  forAccountManagerListing?: boolean;
}) => {
  const user = useUserContext();
  const [query, setQuery] = useState<IWorkspaceUserFilter>(
    forAccountManagerListing ? workspaceAccountManagerPayload : workspaceUsersPayload
  );
  const [isLoading, setLoading] = useState<boolean>(true);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [dashboardObj, setDashboardObj] = useState<DashboardCollectionIdType>();
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);
  const [pinnedDashboardCheck, setPinnedDashboardCheck] = useState([]);
  const [workspaceData, setWorkspaceData] = useState<IUsersDataStatus>({
    workspaceUsers: [],
    totalUsers: 0,
  });

  const data = workspaceData.workspaceUsers.map((user, index) => ({
    id: !isEmpty(user.id) ? index : 'Not Available' + index,
    name: !isEmpty(user.userName?.trim()) ? user.userName : 'Not Available',
    email: user.userId,
    role: user.role ?? 'Not Available',
    status: getUserStatus(user),
    createdOn: user.createdOn ? getDateUsingFormat(user.createdOn.toString(), 'DD MMM YYYY') : '',
    profileImage: user.profileImage ?? '',
  }));

  const sortValue = (field: string): string => {
    switch (field) {
      case 'name':
        return 'user_name';
      case 'email':
        return 'user_id';
      case 'role':
        return 'role';
      default:
        return 'user_name';
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const sortBy = !isEmpty(sortModel) ? sortValue(sortModel[0].field) : undefined;
      const sortOrder = !isEmpty(sortModel) ? sortModel[0].sort : undefined;
      const response = await getWorkspaceUsers({
        ...query,
        page: pageNo - 1,
        size: pageSize,
        sortBy: sortBy,
        sortOrder: sortOrder ?? 'asc',
      });
      const workspace = response.data;
      setWorkspaceData({
        workspaceUsers: workspace.workspaceUsers,
        totalUsers: workspace.totalUsersCount,
      });
    } catch (e) {
      setWorkspaceData({
        workspaceUsers: [],
        totalUsers: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [query, pageNo, pageSize, sortModel]);

  useEffect(() => {
    const dashboardInPinnedList = async () => {
      const responseCollection = await getMyPinnedDashboardsCollection();
      if (responseCollection.data?.length > 0)
        setPinnedDashboardCheck(responseCollection.data?.map((item) => item?.entityId));
    };
    const dashboardAssignedIds = async () => {
      try {
        const response = await fetchDashboardCollectionsAssignedToAccountManagers(true);
        const ids = response.data?.map((item) => item.id);
        setDashboardObj({ dashboardIds: ids, type: 'AccountManager' });
      } catch (error) {
        console.error('Error fetching dashboard collections:', error);
      }
    };
    fetchUsers();
    dashboardInPinnedList();
    dashboardAssignedIds();
  }, [fetchUsers]);
  const navigate = useNavigate();
  const isOverLap = useCallback(
    async (row, isOpenNewTab) => {
      const selectedUserId = row.email;
      const selectedUsername = row.name;
      const currentUserTeams = await getOverlapTeams(selectedUserId);
      const hasOverlap = currentUserTeams.data?.length > 0 ? true : false;
      const url = `/account-manager/details-page?id=${String(
        selectedUserId
      )}&name=${selectedUsername}&isOverLap=${hasOverlap ? 'yes' : 'no'}`;
      isOpenNewTab ? openUrlInNewTab(url) : navigate(url);
    },
    [navigate]
  );

  const columns: GridColDef[] = [
    ...(forAccountManagerListing
      ? [
          {
            field: 'pinUnpin',
            headerName: '',
            width: 50,
            renderCell: (params) => {
              if (dashboardObj?.dashboardIds?.length) {
                return (
                  <PinDashboardCollectionButton
                    filters={null}
                    entityId={params.row?.email}
                    entityName={params.row?.name}
                    homeUnpin={true}
                    setCollect={true}
                    dashbaordsCollectionIds={dashboardObj}
                    isPinned={countMatchingItems(params) === dashboardObj?.dashboardIds?.length}
                  />
                );
              }
            },
          },
        ]
      : []),
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      align: 'left',
      sortable: false,
      renderCell: (params) => {
        return (
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              <WsAvatar
                src={params.row.profileImage ?? PersonAvatar}
                active={params.row.status == 'Activated'}
                size="small"
                variant="circular"
              />
            </Grid>
            <Grid item>
              <Link
                component={RouterLink}
                to={
                  window.location.pathname === '/workspace-users-and-teams/users-list'
                    ? `/users/details-page?id=${params?.row?.id}&email=${params?.row?.email}&isLink=true&isUserAnalytics=true&goBackUrl=${window.location.pathname}`
                    : `/account-manager/details-page?id=${params?.row?.email}&name=${params?.row?.name}&goBackUrl=${window.location.pathname}`
                }
              >
                <WsTypography color={WsTypoColors.PRIMARY750}>
                  <Box
                    component="span"
                    sx={{
                      cursor: 'pointer',
                      display: 'inline-block',
                    }}
                  >
                    {params?.row?.name}
                  </Box>
                </WsTypography>
              </Link>
            </Grid>
          </Grid>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      renderCell: (params) => <WsTypography>{params.value}</WsTypography>,
    },
    ...(!forAccountManagerListing
      ? [
          {
            field: 'role',
            headerName: 'Role',
            flex: 1,
            renderCell: (params) => <WsTypography>{params.value}</WsTypography>,
          },
        ]
      : []),
    {
      field: 'createdOn',
      headerName: 'Date Registered',
      flex: 1,
      renderCell: (params) => <WsTypography>{params.value}</WsTypography>,
    },
    {
      field: 'status',
      headerName: 'Invite Status',
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <WsBadge
          label={params?.value}
          badgeSize={WsBadgeSize.LARGE}
          badgeType={WsBadgetype.SUCCESS}
        />
      ),
    },
  ];

  const countMatchingItems = (params) =>
    pinnedDashboardCheck?.filter((item) => item === params.row?.email).length;

  const loadUsers = async () => await triggerUsersImport();

  const handleOnNameClick = (row: any, isOpenNewTab = false) => {
    isOverLap(row, isOpenNewTab);
  };

  const rowValue = useRef({});
  const defaultContextMenuValues = { showContext: false, x: 0, y: 0 };
  const [contextMenuDetails, setContextMenuDetails] = useState(defaultContextMenuValues);
  const menuItems = [
    {
      name: 'Open in new tab',
      onclick: () => {
        handleOnNameClick(rowValue.current, true);
      },
    },
  ];

  const paginationComponent = (
    <StyledPaginationBox>
      <Box ml={2} display="flex" gap={1}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>Total Count:</WsTypography>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
          {workspaceData.totalUsers}
        </WsTypography>
      </Box>
      {workspaceData.totalUsers > 10 && (
        <WsDataGridPagination
          count={Math.ceil(workspaceData.totalUsers / pageSize)}
          page={pageNo}
          onPageChange={(_$, newPage) => setPageNo(newPage)}
          pageSize={pageSize}
          onPageSizeChange={({ target: { value } }) => setPageSize(value)}
        />
      )}
    </StyledPaginationBox>
  );

  return (
    <Grid
      container
      sx={{
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
        bgcolor: WsColors.White,
        borderRadius: 2,
        padding: 2,
        overflow: 'hidden',
      }}
    >
      {user?.workspaceUser?.id === NORTHHILLPARTNERS_WORKSPACE_ID && (
        <Grid item xs={12} p={1}>
          <WsButton onClick={loadUsers} size="medium">
            Start Import
          </WsButton>
        </Grid>
      )}
      {query && (
        <Grid item xs={12}>
          <UsersFilterDetailsNew
            query={{ ...query, page: 0, size: 50 }}
            setQuery={setQuery}
            forAccountManagerListing={forAccountManagerListing}
          />
        </Grid>
      )}
      {contextMenuDetails.showContext && (
        <Grid item xs={12}>
          <WsContextMenuHandler
            menuItems={menuItems}
            top={contextMenuDetails.y}
            left={contextMenuDetails.x}
            setContextMenuDetails={setContextMenuDetails}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        {isLoading ? (
          <Box
            sx={{
              '& .MuiCard-root': {
                boxShadow: 'none',
                elevation: 0,
              },
            }}
          >
            <WsListingLoader />
          </Box>
        ) : (
          <>
            {paginationComponent}
            <StyledDataGrid
              autoHeight
              hideFooter
              columns={columns}
              rows={data ?? []}
              sortModel={sortModel}
              onSortModelChange={(newSortModel) => {
                setPageNo(1);
                setSortModel(newSortModel);
              }}
              components={
                forAccountManagerListing
                  ? {
                      NoRowsOverlay: () => (
                        <Box width={'100%'} height="100%">
                          <NoDataFoundCard
                            text="No Data Found"
                            subElement={
                              <WsTypography color={WsTypoColors.PRIMARY}>
                                {'Tip: Try clearing applied filters if any'}
                              </WsTypography>
                            }
                          />
                        </Box>
                      ),
                    }
                  : {}
              }
            />
            {workspaceData.totalUsers > 10 && paginationComponent}
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default AllUsersInTeamsNew;
